/**
 * Color utility functions for WebsiteBuilderV2
 * Handles dynamic text color based on background colors for optimal contrast
 *
 * Note: This file is maintained for backward compatibility.
 * New color system is available in enhancedColorUtils.js
 */

/**
 * Converts a hex color to RGB values
 * @param {string} hex - Hex color string (e.g., "#FF0000" or "FF0000")
 * @returns {object} RGB object with r, g, b values
 */
export const hexToRgb = (hex) => {
  // Remove # if present
  const cleanHex = hex.replace("#", "")

  // Handle 3-digit hex
  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16)
    const g = parseInt(cleanHex[1] + cleanHex[1], 16)
    const b = parseInt(cleanHex[2] + cleanHex[2], 16)
    return { r, g, b }
  }

  // Handle 6-digit hex
  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16)
    const g = parseInt(cleanHex.substring(2, 4), 16)
    const b = parseInt(cleanHex.substring(4, 6), 16)
    return { r, g, b }
  }

  // Default to black if invalid
  return { r: 0, g: 0, b: 0 }
}

/**
 * Converts RGB values to HSL
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {object} HSL object with h, s, l values
 */
export const rgbToHsl = (r, g, b) => {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h,
    s,
    l = (max + min) / 2

  if (max === min) {
    h = s = 0 // achromatic
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  return { h: h * 360, s: s * 100, l: l * 100 }
}

/**
 * Calculates the relative luminance of a color
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {number} Relative luminance (0-1)
 */
export const getRelativeLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

/**
 * Determines if a color is dark or light based on its luminance
 * @param {string} color - Color in hex format (e.g., "#FF0000")
 * @returns {boolean} true if dark, false if light
 */
export const isDarkColor = (color) => {
  if (!color) return false

  const rgb = hexToRgb(color)
  const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b)

  // Threshold of 0.5 - colors with luminance < 0.5 are considered dark
  return luminance < 0.5
}

/**
 * Gets the appropriate text color based on background color
 * @param {string} backgroundColor - Background color in hex format
 * @param {object} theme - Theme object with color properties
 * @param {object} options - Additional options
 * @param {boolean} options.forceLight - Force light text regardless of background
 * @param {boolean} options.forceDark - Force dark text regardless of background
 * @param {string} options.lightTextColor - Custom light text color
 * @param {string} options.darkTextColor - Custom dark text color
 * @returns {string} Appropriate text color
 */
export const getContrastingTextColor = (backgroundColor, theme = {}, options = {}) => {
  const {
    forceLight = false,
    forceDark = false,
    lightTextColor = theme?.colors?.textLight || "#FFFFFF",
    darkTextColor = theme?.colors?.textDark || "#1F2937"
  } = options

  // If forced, return the appropriate color
  if (forceLight) return lightTextColor
  if (forceDark) return darkTextColor

  // If no background color, use default dark text
  if (!backgroundColor) return darkTextColor

  // Check if background is dark
  const isDark = isDarkColor(backgroundColor)

  // Return contrasting color
  return isDark ? lightTextColor : darkTextColor
}

/**
 * WCAG contrast ratio between two hex colors.
 * @param {string} hexA
 * @param {string} hexB
 * @returns {number} Contrast ratio (1–21)
 */
export const getContrastRatio = (hexA, hexB) => {
  const rgbA = hexToRgb(hexA)
  const rgbB = hexToRgb(hexB)
  const luminanceA = getRelativeLuminance(rgbA.r, rgbA.g, rgbA.b)
  const luminanceB = getRelativeLuminance(rgbB.r, rgbB.g, rgbB.b)
  const lighter = Math.max(luminanceA, luminanceB)
  const darker = Math.min(luminanceA, luminanceB)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Contrast-safe text color for filled primary CTAs across Website Builder v2.
 * Uses true white (#FFFFFF) for labels — not theme textLight, which is often a soft cream
 * meant for hero copy on photos and can fail contrast checks on mid-tone primary colors.
 * Very light button fills still get dark text.
 * @param {string | undefined} backgroundColor - Background color in hex format
 * @param {object} theme - Theme object with colors.textDark
 * @returns {string} Hex color for button text
 */
export const getContrastSafeButtonTextColor = (backgroundColor, theme = {}) => {
  const lightText = "#FFFFFF"
  const darkText = theme?.colors?.textDark || "#1F2937"
  if (!backgroundColor) return lightText

  const lightContrast = getContrastRatio(backgroundColor, lightText)
  const darkContrast = getContrastRatio(backgroundColor, darkText)
  // Primary-style fills: prefer white unless the fill is so light that dark reads clearly better.
  if (lightContrast >= 2) return lightText
  return darkContrast >= lightContrast ? darkText : lightText
}

/**
 * Gets text color for section based on various factors
 * @param {object} section - Section object
 * @param {object} theme - Theme object
 * @param {object} options - Additional options
 * @returns {string} Appropriate text color
 */
export const getSectionTextColor = (section, theme = {}, options = {}) => {
  const settings = section?.settings || {}
  const { hasBackgroundImage = false, isInWhiteContainer = false, layout = null } = options

  // If content is in a white container, use dark text
  if (isInWhiteContainer) {
    return getContrastingTextColor(null, theme, { forceDark: true })
  }

  // If there's a background image or it's a full image layout, use light text
  if (hasBackgroundImage || layout === "full_image" || (layout && layout.includes("image"))) {
    return getContrastingTextColor(null, theme, { forceLight: true })
  }

  // Check if section has custom background color
  const showBackgroundColor = settings.showBackgroundColor || false
  const backgroundColor = settings.backgroundColor

  if (showBackgroundColor && backgroundColor) {
    return getContrastingTextColor(backgroundColor, theme)
  }

  // Check theme background color
  const themeBackgroundColor = theme?.colors?.background || theme?.colors?.bgLight

  if (themeBackgroundColor) {
    return getContrastingTextColor(themeBackgroundColor, theme)
  }

  // Default to dark text
  return getContrastingTextColor(null, theme, { forceDark: true })
}

/**
 * Gets text color for specific text elements within a section
 * @param {string} elementType - Type of text element (heading, body, caption, etc.)
 * @param {string} backgroundColor - Background color
 * @param {object} theme - Theme object
 * @param {object} options - Additional options
 * @returns {string} Appropriate text color
 */
export const getElementTextColor = (elementType, backgroundColor, theme = {}, options = {}) => {
  const {
    lightTextColor = theme?.colors?.textLight || "#FFFFFF",
    darkTextColor = theme?.colors?.textDark || "#1F2937"
  } = options

  // For certain elements, we might want different behavior
  switch (elementType) {
    case "heading":
      return getContrastingTextColor(backgroundColor, theme, {
        lightTextColor: theme?.colors?.headingLight || lightTextColor,
        darkTextColor: theme?.colors?.headingDark || darkTextColor
      })
    case "body":
      return getContrastingTextColor(backgroundColor, theme, {
        lightTextColor: theme?.colors?.bodyLight || lightTextColor,
        darkTextColor: theme?.colors?.bodyDark || darkTextColor
      })
    case "caption":
      return getContrastingTextColor(backgroundColor, theme, {
        lightTextColor: theme?.colors?.captionLight || lightTextColor,
        darkTextColor: theme?.colors?.captionDark || darkTextColor
      })
    default:
      return getContrastingTextColor(backgroundColor, theme)
  }
}

/**
 * Validates if a color string is a valid hex color
 * @param {string} color - Color string to validate
 * @returns {boolean} true if valid hex color
 */
export const isValidHexColor = (color) => {
  if (!color || typeof color !== "string") return false

  const cleanColor = color.replace("#", "")
  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanColor)
}

/**
 * Gets a safe fallback color if the provided color is invalid
 * @param {string} color - Color to check
 * @param {string} fallback - Fallback color
 * @returns {string} Valid color string
 */
export const getSafeColor = (color, fallback = "#FFFFFF") => (isValidHexColor(color) ? color : fallback)
